import React, { useState, useEffect } from "react";
import { useSale } from "../contexts/SaleContext";
import toast from "../utils/toast";

const SetCategoryDiscount = () => {
    const { categoryDiscounts, saveCategoryDiscounts, categories } = useSale();
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [discountInput, setDiscountInput] = useState("");
    const [saving, setSaving] = useState(false);

    // Search & Pagination state
    const [search, setSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(5);
    const PAGE_SIZE_OPTIONS = [5, 15, 25, 50, 100];

    const filteredCategories = categories.filter((cat) =>
        cat.toLowerCase().includes(search.toLowerCase())
    );

    const discountedCategories = categories.filter(
        (cat) => categoryDiscounts[cat] !== undefined && categoryDiscounts[cat] !== null
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [search, pageSize]);

    const totalPages = Math.max(1, Math.ceil(filteredCategories.length / pageSize));
    const paginatedCategories = filteredCategories.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    // ── Discount Operations ──

    const handleSelect = (category) => {
        setSelectedCategory(category);
        setDiscountInput(
            categoryDiscounts[category] !== undefined && categoryDiscounts[category] !== null
                ? String(categoryDiscounts[category])
                : ""
        );
    };

    const handleSaveDiscount = async () => {
        if (!selectedCategory) return;
        const value = parseFloat(discountInput);
        if (isNaN(value) || value < 0 || value > 100) {
            toast.error("Enter a valid discount between 0 and 100");
            return;
        }
        setSaving(true);
        try {
            const updatedDiscounts = { ...categoryDiscounts, [selectedCategory]: value };
            await saveCategoryDiscounts(updatedDiscounts);
            toast.success(`Discount of ${value}% set for category "${selectedCategory}"`);
            setSelectedCategory(null);
            setDiscountInput("");
        } catch (err) {
            console.error(err);
            toast.error("Failed to save discount");
        } finally {
            setSaving(false);
        }
    };

    const handleClearDiscount = async (category) => {
        try {
            const updatedDiscounts = { ...categoryDiscounts };
            delete updatedDiscounts[category];
            await saveCategoryDiscounts(updatedDiscounts);
            toast.success(`Discount cleared for category "${category}"`);
            if (selectedCategory === category) {
                setSelectedCategory(null);
                setDiscountInput("");
            }
        } catch (err) {
            console.error(err);
            toast.error("Failed to clear discount");
        }
    };

    return (
        <div className="upload-page">
            <div className="spd-section-card">
                <h2 className="spd-section-heading">Category Discounts</h2>
                <p className="admin-page-subtitle">
                    Apply a specific discount percentage to all products within a category.
                    To add or remove categories, use <strong>Manage Categories</strong>.
                </p>

                {/* Search */}
                <div className="stock-search-bar" style={{ marginBottom: 18 }}>
                    <input
                        type="text"
                        className="stock-search-input"
                        placeholder="Search categories..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {search && (
                        <button className="stock-clear-search" onClick={() => setSearch("")}>
                            ✕
                        </button>
                    )}
                </div>

                <div className="table-scroll-wrapper">
                    <table className="products-table">
                        <thead>
                            <tr>
                                <th>Category Name</th>
                                <th>Current Discount</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedCategories.length === 0 ? (
                                <tr>
                                    <td colSpan="3" className="no-results">
                                        {search ? "No categories match your search." : "No categories found."}
                                    </td>
                                </tr>
                            ) : (
                                paginatedCategories.map((cat) => (
                                    <tr
                                        key={cat}
                                        className={selectedCategory === cat ? "row-being-edited" : ""}
                                    >
                                        <td className="product-name-cell">{cat}</td>
                                        <td>
                                            {categoryDiscounts[cat] !== undefined ? (
                                                <span className="spd-discount-badge">
                                                    {categoryDiscounts[cat]}%
                                                </span>
                                            ) : (
                                                <span style={{ color: "#aaa", fontSize: 12 }}>Not set</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="stock-action-btns">
                                                <button
                                                    className="spd-select-btn"
                                                    onClick={() => handleSelect(cat)}
                                                    disabled={selectedCategory === cat}
                                                >
                                                    {selectedCategory === cat ? "Selected" : "Set Discount"}
                                                </button>
                                                {categoryDiscounts[cat] !== undefined && (
                                                    <button
                                                        className="spd-clear-btn"
                                                        onClick={() => handleClearDiscount(cat)}
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {filteredCategories.length > 0 && (
                    <div className="pagination-bar" style={{ marginTop: "16px" }}>
                        <div className="pagination-info">
                            Showing {Math.min((currentPage - 1) * pageSize + 1, filteredCategories.length)}–
                            {Math.min(currentPage * pageSize, filteredCategories.length)} of {filteredCategories.length}
                        </div>
                        <div className="pagination-controls">
                            <button className="page-btn" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>«</button>
                            <button className="page-btn" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>‹</button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                                .reduce((acc, p, idx, arr) => {
                                    if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                    acc.push(p);
                                    return acc;
                                }, [])
                                .map((item, i) =>
                                    item === "..." ? (
                                        <span key={`ellipsis-${i}`} className="page-ellipsis">…</span>
                                    ) : (
                                        <button
                                            key={item}
                                            className={`page-btn${currentPage === item ? " active" : ""}`}
                                            onClick={() => setCurrentPage(item)}
                                        >
                                            {item}
                                        </button>
                                    )
                                )}
                            <button className="page-btn" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>›</button>
                            <button className="page-btn" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>»</button>
                        </div>
                        <div className="pagination-size">
                            <label>Rows per page:</label>
                            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}>
                                {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* Discount Input Panel */}
            {selectedCategory && (
                <div className="spd-section-card spd-input-panel">
                    <h2 className="spd-section-heading">
                        <i className="fa-solid fa-percent" style={{ marginRight: 8, color: "var(--red)" }}></i>
                        Set Discount for &ldquo;{selectedCategory}&rdquo;
                    </h2>
                    <div className="spd-input-row">
                        <div className="spd-discount-field">
                            <label className="spd-label">Discount (%)</label>
                            <div className="spd-input-group">
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.5"
                                    className="spd-number-input"
                                    placeholder="e.g. 15"
                                    value={discountInput}
                                    onChange={(e) => setDiscountInput(e.target.value)}
                                />
                                <span className="spd-percent-sign">%</span>
                            </div>
                        </div>
                        <div className="spd-actions">
                            <button className="spd-save-btn" onClick={handleSaveDiscount} disabled={saving}>
                                {saving ? "Saving…" : "Apply Discount"}
                            </button>
                            <button
                                className="spd-cancel-btn"
                                onClick={() => { setSelectedCategory(null); setDiscountInput(""); }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Categories with Active Discounts */}
            <div className="spd-section-card">
                <h2 className="spd-section-heading">
                    <i className="fa-solid fa-check-circle" style={{ marginRight: 8, color: "#1a7a40" }}></i>
                    Categories with Active Discounts
                    <span className="products-count" style={{ marginLeft: 10 }}>
                        {discountedCategories.length}
                    </span>
                </h2>
                {discountedCategories.length === 0 ? (
                    <div className="no-products">No categories have an active discount set yet.</div>
                ) : (
                    <div className="table-scroll-wrapper">
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Category Name</th>
                                    <th>Discount</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {discountedCategories.map((cat) => {
                                    const disc = parseFloat(categoryDiscounts[cat]);
                                    return (
                                        <tr key={cat}>
                                            <td className="product-name-cell">{cat}</td>
                                            <td>
                                                <span className="spd-discount-badge">{disc}%</span>
                                            </td>
                                            <td>
                                                <div className="stock-action-btns">
                                                    <button className="spd-select-btn" onClick={() => handleSelect(cat)}>
                                                        Edit
                                                    </button>
                                                    <button className="spd-clear-btn" onClick={() => handleClearDiscount(cat)}>
                                                        Clear
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SetCategoryDiscount;